import { format } from 'date-fns';
import { Api } from '../api';
import { application } from '../endpoints';
import type {
  ApplicationEntity,
  ApplicationItem,
  ApplicationPdfRequest,
  ApplicationSearchParams,
  AssignApplicationRequest,
  AssignManualApplicationRequest,
  ManualApplicationDownloadParams,
  ManualApplicationEntity,
  ManualApplicationFileSearchParams,
  ManualApplicationSearchParams,
  RejectManualApplicationFileRequest,
  SupplementaryApplicationEntity,
} from './models';
import { IFile } from '../file';

const getDatePart = (date: Date | undefined) => {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return [year, pad2(month), pad2(day)].join('-');
};

const pad2 = (v: number): string => {
  if (v < 10) return '0' + v.toString();
  return v.toString();
};

export class ApplicationApi extends Api {
  static getApplications = (searchParams: ApplicationSearchParams = {}, signal?: AbortSignal) => {
    const params = {
      ApplicationName: searchParams.ApplicationName,
      Passport: searchParams.Passport,
      ModifiedOn: getDatePart(searchParams.ModifiedOn),
      Status: searchParams.status,
    };

    return this.get<ApplicationItem[]>({ url: application.getApplications(), queryParams: params, signal });
  };

  static getApplicationsByCustomer = (customerId: string, signal?: AbortSignal) =>
    this.get<ApplicationItem[]>({ url: application.getApplication(), queryParams: { customerId }, signal });

  static getSupplementaryApplication = (applicationId: string) =>
    this.get<SupplementaryApplicationEntity>({ url: application.getSupplementApplication(applicationId) });

  static getMainApplication = (applicationId: string) => this.get<ApplicationEntity>({ url: application.getMainApplication(applicationId) });

  static searchManualApplications = (searchParams: ManualApplicationSearchParams = {}, signal?: AbortSignal) =>
    this.get<ManualApplicationEntity[]>({ url: application.getManualApplication(), queryParams: searchParams, signal });

  static getPdf = (queryParams: ApplicationPdfRequest, signal?: AbortSignal) =>
    this.get<IFile>({ url: application.pdf(), queryParams, headers: { Accept: 'application/pdf' }, signal });

  static saveSupplement = (applicationData: SupplementaryApplicationEntity) => {
    const data = { ...applicationData };
    //@ts-expect-error types
    delete data.insertedDate;
    if (typeof data.securityAnswer === 'number') data.securityAnswer = (data.securityAnswer as number).toString();
    // @ts-expect-error types
    if (data.anticipatedMonthlySpend === '') delete data.anticipatedMonthlySpend;

    return this.post<SupplementaryApplicationEntity, string>({ url: application.saveSupplement(), data });
  };

  static saveApplication = (applicationData: ApplicationEntity) => {
    const data = { ...applicationData };
    // @ts-expect-error types
    delete data.processedDate;
    // @ts-expect-error types
    delete data.insertedDate;
    if (typeof data.passportExpiryDate === 'object' && (data.passportExpiryDate as object) instanceof Date)
      data.passportExpiryDate = format(data.passportExpiryDate, 'yyyy-MM-dd');
    if (typeof data.dob === 'object' && (data.dob as object) instanceof Date) data.dob = format(data.dob, 'yyyy-MM-dd');
    if (typeof data.securityAnswer === 'number') data.securityAnswer = (data.securityAnswer as number).toString();
    // @ts-expect-error types
    if (data.anticipatedMonthlySpend === '') delete data.anticipatedMonthlySpend;

    return this.post<ApplicationEntity, string>({ url: application.saveApplication(), data });
  };

  static approveCreditLimit = (applicationId: string, signal?: AbortSignal) =>
    this.post({ url: application.approveCreditLimit(applicationId), signal });

  static rejectCreditLimit = (applicationId: string, signal?: AbortSignal) =>
    this.post({ url: application.rejectCreditLimit(applicationId), signal });

  static submitApplication = (applicationId: string) => this.post({ url: application.submitApplication(applicationId) });

  static rejectApplication = (applicationId: string, signal?: AbortSignal) =>
    this.post({ url: application.rejectApplication(applicationId), signal });

  static assignApplication = (request: AssignApplicationRequest, signal?: AbortSignal) =>
    this.post({ url: application.assignApplication(request.applicationId), data: request.data, signal });

  static assignManualApplication = (data: AssignManualApplicationRequest, signal?: AbortSignal) =>
    this.post({ url: application.assignManualApplication(), data, signal });

  static rejectManualApplicationFile = (searchParams: ManualApplicationFileSearchParams, signal?: AbortSignal) =>
    this.post<RejectManualApplicationFileRequest>({
      url: application.rejectManualApplicationFile(searchParams.fileId),
      data: { comment: searchParams.comment || '' },
      signal,
    });

  static submitManualApplication = (manualApplicationId: string, signal?: AbortSignal) =>
    this.post({ url: application.submitManualApplication(), data: { manualApplicationId }, signal });

  static approveManualApplication = (searchParams: ManualApplicationDownloadParams, signal?: AbortSignal) =>
    this.post<ManualApplicationDownloadParams>({ url: application.completeManualApplication(), data: searchParams, signal });

  static rejectManualApplication = (searchParams: ManualApplicationDownloadParams, signal?: AbortSignal) =>
    this.post<ManualApplicationDownloadParams>({ url: application.rejectManualApplication(), data: searchParams, signal });
}
